import { Link } from '@tanstack/react-router';
import { GitBranch } from 'lucide-react';
import {
  ChartEmpty,
  HeatMapLegend,
  PostureLegend,
  ScoreHeatMap,
  StackedColumns,
  TrendChart,
  VERDICT_LABEL,
  VERDICT_POSTURE,
} from '@/components/ui/charts';
import { PostureChip } from '@/components/ui/posture-chip';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceButton } from '@/components/ui/workspace';
import { useHealthReadinessQuery } from '@/lib/api/health-readiness';
import { formatDate } from '@/lib/utils/format';
import { useHrCapture } from '../layout';
import { POSTURE_LABEL } from '../model';
import { readinessTrendPoints } from '../overview/overview-model';
import { CardMeta, OverviewCard } from '../overview/parts';
import {
  columnHeight,
  hasTodayColumn,
  heatColumns,
  heatGroups,
  postureColumns,
} from './snapshots-model';
import { Td, Th } from './table-cells';

/**
 * Health & Readiness → Snapshots & trend (T-0244 / E22 design §2;
 * prototype `snapshotsTab()`), top to bottom: readiness over time (the
 * weighted total at each snapshot, then today), domain ratings over time
 * beside the verdict at each snapshot and who captured it, and every
 * criterion's score across the snapshots with its change.
 *
 * Everything comes from the composite read: `trend[]` (each snapshot's
 * frozen total, verdict, posture counts and capturer), `criteria[].trend`
 * (scores by snapshot id, null where a snapshot did not hold the
 * criterion) and `trend_change`. Capture snapshot is the header's (the
 * layout's one capture); a new engagement gets it here as its first step.
 */
export function SnapshotsPane({
  engagementId,
  today = new Date(),
}: {
  engagementId: string;
  /** Today, for the live column and the trend's last point. */
  today?: Date;
}) {
  const { data: view } = useHealthReadinessQuery(engagementId);
  const { capture, capturing } = useHrCapture();
  if (!view) return null;

  if (view.trend.length === 0) {
    return (
      <div data-testid="snapshots-pane">
        <ChartEmpty title="No snapshots yet" className="py-14">
          A snapshot freezes every rating, score and verdict of its day and never changes. The
          trend, the rating history and the criteria heat map start with the first one.
          <div className="mt-3">
            {capture ? (
              <WorkspaceButton variant="primary" onClick={capture} disabled={capturing}>
                {capturing ? <Spinner /> : <GitBranch className="size-3.5" aria-hidden="true" />}
                {capturing ? 'Capturing…' : 'Capture the first snapshot'}
              </WorkspaceButton>
            ) : (
              <span>An engagement lead captures snapshots.</span>
            )}
          </div>
        </ChartEmpty>
      </div>
    );
  }

  const live = hasTodayColumn(view, today);
  const readiness = view.verdict.readiness;
  const counts = view.verdict.posture.counts;

  return (
    <div className="flex flex-col gap-4" data-testid="snapshots-pane">
      {readiness ? (
        <OverviewCard
          title="Readiness over time"
          aside={
            <CardMeta>weighted total at each snapshot{live ? ', then today (live)' : ''}</CardMeta>
          }
        >
          <TrendChart
            motionKey="snapshots"
            points={readinessTrendPoints(view, today)}
            thresholds={readiness.thresholds}
            width={1000}
            height={260}
          />
        </OverviewCard>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <OverviewCard
          title="Domain ratings over time"
          aside={
            <PostureLegend
              counts={{
                critical: counts.critical,
                'at-risk': counts.atRisk,
                healthy: counts.healthy,
                unassessed: counts.unassessed,
              }}
            />
          }
        >
          <StackedColumns
            motionKey="snapshots"
            columns={postureColumns(view, live)}
            total={columnHeight(view)}
          />
        </OverviewCard>

        <OverviewCard title="Verdict at each snapshot">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px] text-ink-700">
              <caption className="sr-only">The verdicts each snapshot froze, newest first</caption>
              <thead>
                <tr>
                  <Th>Snapshot</Th>
                  <Th>Posture</Th>
                  <Th className="text-right">Readiness</Th>
                  <Th>Verdict</Th>
                  <Th>By</Th>
                </tr>
              </thead>
              <tbody>
                {view.trend
                  .slice()
                  .reverse()
                  .map((t) => (
                    <tr key={t.snapshotId} className="group">
                      <Td className="whitespace-nowrap">{formatDate(t.capturedAt)}</Td>
                      <Td>
                        <PostureChip status={t.posture.status} dot>
                          {POSTURE_LABEL[t.posture.status]}
                        </PostureChip>
                      </Td>
                      <Td className="text-right tabular-nums">
                        {t.totalPercent === null ? '—' : `${Math.round(t.totalPercent)}%`}
                      </Td>
                      <Td>
                        {t.recommendation ? (
                          <PostureChip status={VERDICT_POSTURE[t.recommendation]} dot>
                            {VERDICT_LABEL[t.recommendation]}
                          </PostureChip>
                        ) : (
                          <PostureChip status="unassessed" dot>
                            Not all scored
                          </PostureChip>
                        )}
                      </Td>
                      <Td>{t.capturedByName ?? '—'}</Td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </OverviewCard>
      </div>

      {readiness && view.criteria.length > 0 ? (
        <OverviewCard
          title="Criteria across snapshots"
          aside={<HeatMapLegend thresholds={readiness.thresholds} />}
        >
          <div className="overflow-x-auto">
            <ScoreHeatMap
              motionKey="snapshots"
              columns={heatColumns(view, live)}
              groups={heatGroups(view, live)}
              thresholds={readiness.thresholds}
              renderName={(row) => (
                <Link
                  to="/engagements/$engagementId/health-readiness/criteria/$criterionId"
                  params={{ engagementId, criterionId: row.id }}
                  className="text-ink-900 no-underline hover:underline hover:underline-offset-[3px]"
                >
                  {row.name}
                </Link>
              )}
            />
          </div>
        </OverviewCard>
      ) : null}
    </div>
  );
}
